import React, { useEffect, useState } from "react";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import {
  TextFilter,
  BooleanFilter,
  TableAbstract,
} from "shared/components/Components";
import { useMercuryContext } from "../../context";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { descriptionColumn } from "shared/table/columnsConfig";
import { checkPermissions } from "../../common/utils/checkPermissions";
import PermissionWrapper from "shared/components/PermissionWrapper";
import TeamRequest from "./TeamRequest";
import OwnerFilter from "filters/Owner.filter";
import CustomerApplicationsFilter from "filters/CustomerApplications.filter";
import { Tag } from "antd";
import { Application, ManageTeamsProps, Member, MemberOption, Team } from "./TeamInterfaces";
class ManageTeams extends TableAbstract<Team> {
  applicationsList: Application[] = [];
  membersList: MemberOption[] = [];

  constructor(props: ManageTeamsProps) {
    super(props);
    this.applicationsList = props.applicationsList || [];
    this.membersList = props.membersList || [];
  }
  defaultSortField = "name";

  FilterComponents = [
    { id: "name", name: "name", title: "Team Name", component: TextFilter, active: true },
    { id: "description", name: "description", title: "Description", component: TextFilter, active: true },
    {
      id: "customerApplication", name: "customerApplication", title: "Customer Application", component: (props: any) => (
        <CustomerApplicationsFilter {...props} data={(this.props as ManageTeamsProps)?.applicationsList} />
      ), active: true
    },
    { id: "members", name: "members", title: "Members", component: (props: any) => <OwnerFilter {...props} data={(this.props as ManageTeamsProps)?.membersList} />, active: true },
    { id: "isActive", name: "isActive", title: "Active Status", component: BooleanFilter, active: true },
  ];

  renderTags(text: string | string[]) {
    if (typeof text === "string") {
      return (
        <Tag
          color="var(--tertiary-color)"
          style={{ margin: "0 2px 2px 0", padding: "0 4px" }}
        >
          {text}
        </Tag>
      );
    }
    return text?.map((item, index) => (
      <Tag
        key={index}
        color="var(--tertiary-color)"
        style={{ margin: "0 2px 2px 0", padding: "0 4px" }}
      >
        {item}
      </Tag>
    ));
  }

  columns = [
    { id: "name", title: "Team Name", dataIndex: "name", sorter: true },
    descriptionColumn,
    {
      id: "customerApplication",
      title: "Customer Application",
      dataIndex: "customerApplication",
      render: (record: any) => {
        return record ? this.renderTags(record) : "Error: Could not match Id with name"
      },
    },
    {
      id: "members",
      title: "Members",
      dataIndex: "members",
      sorter: true,
      width: 400,
      fixed: undefined,
      render: (members: string[] | string) => {
        return members !== "Not set" ? this.renderTags(members) : members;
      },
    },
    {
      id: "isActive",
      title: "Active Status",
      dataIndex: "isActive",
      sorter: true,
      render: (_text: any, record: Team) =>
        record.isActive ? <CheckOutlined style={{ color: "var(--tertiary-color)" }} /> :
          <CloseOutlined style={{ color: "var(--red)" }} />,
    },
  ];

  renderActions() {
    const userPermissions = this.userContext.userDetails?.permissions || [];
    const hasCreateAccess = checkPermissions(userPermissions, [
      { customerApplication: "Mercury", permissionName: "Manage Teams", action: "create" },
    ]);
    const hasEditAccess = checkPermissions(userPermissions, [
      { customerApplication: "Mercury", permissionName: "Manage Teams", action: "edit" },
    ]);

    const selectedTeam = this.state?.selectedRows[0];
    return (
      <>
        <PermissionWrapper tooltip={
          !hasCreateAccess ? "No permission to add a new team." : ''
        } hasPermissions={hasCreateAccess}
          children={
            <TeamRequest
              applicationsList={(this.props as ManageTeamsProps).applicationsList}
              membersList={(this.props as ManageTeamsProps).membersList}
              buttonLabel="Add Team"
              hasPermissions={hasCreateAccess}
              disabled={!hasCreateAccess}
              refresh={() => this.reload()}
            />
          }
        />

        <PermissionWrapper
          tooltip={
            !hasEditAccess ? "No permission to edit a team."
              : this.state.selectedRows.length !== 1 ? "Select 1 team to enable edit option" : ''
          }
          hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
          children={
            <TeamRequest
              initialValues={{
                ...selectedTeam,
                customerApplication: selectedTeam?.customerApplicationId || selectedTeam?.customerApplication,
                members: Array.isArray(selectedTeam?.membersId)
                  ? selectedTeam?.membersId.map(String)
                  : Array.isArray(selectedTeam?.members)
                    ? selectedTeam?.members.map(String)
                    : [],
              }}
              applicationsList={(this.props as ManageTeamsProps).applicationsList}
              membersList={(this.props as ManageTeamsProps).membersList}
              buttonLabel="Edit"
              disabled={!hasEditAccess || this.state.selectedRows.length !== 1}
              hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
              refresh={() => this.reload()}
            />
          }
        />
      </>
    );
  }

  rowClassName = (_record: Team): string => "row";

  async fetch(): Promise<any> {
    const { sortOrder, sortField, currentPage, itemsPerPage } = this.state;
    const { applicationsList, membersList } = this.props as ManageTeamsProps;
    const requestPayload = {
      filter: this.filtersToQuery(),
      pagination: {
        pageNumber: currentPage || 1,
        resultsPerPage: itemsPerPage,
      },
      sort: {
        [sortField || "name"]: sortOrder === "ascend" ? 1 : -1,
      },
    };
    
    const response = await this.userContext.umsClient.post(`/ums-v1/teams/search`, requestPayload);
    const documents: Team[] = response?.data?.documents || [];
    const filteredDocuments = documents.map((team) => {
      const app = applicationsList.find((a) => a._id === team.customerApplication);
      const appName = app ? app.name : null;
      const memberNames: any = (team.members || [])
        .map((id) => {
          const member = membersList.find((m) => m.value === id);
          return member?.title;
        })
        .filter(Boolean);
      return {
        ...team,
        customerApplication: appName,
        members: memberNames.length > 0 ? memberNames : "Not set",
        customerApplicationId: team?.customerApplication,
        membersId: team?.members,
      };
    });
    const meta = response?.data?.meta ?? {
      total: 0,
      page: currentPage,
      pages: 1,
      skip: 0,
      limit: itemsPerPage,
      match: {},
      sort: { createdAt: -1 },
    };

    return {
      data: {
        documents: filteredDocuments,
        meta
      },
    };
  }

  pageTitle = "Manage Teams - Mercury © RWS";
  title = "Manage Teams";
  pageClass = "teamsTable";
}

const ManageTeamsTable = () => {

  const context = useMercuryContext();
  const breaks = useBreakpoint();

  const [applicationsList, setApplicationsList] = useState<Application[]>([]);
  const [membersList, setMembersList] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    const fetchData = async () => {
      try {

        const [{ data: appData }, { data: membersData }] = await Promise.all([
          context.coreClient.get("core-v1/customerApplications/paginate?page=1&limit=500"),
          context.umsClient.get("/ums-v1/users"),
        ]);

        const applicationsList: Application[] = appData?.[0]?.documents || [];
        setApplicationsList(applicationsList);
        const validAppIds = new Set(applicationsList.map(app => app._id));

        const filteredMembersList: MemberOption[] = (membersData as Member[]).filter(member =>
          member.isActive &&
          member.customerApplications?.some(app => validAppIds.has(app._id))
        ).map(member => ({
          title: member.name,
          value: member._id,
          customerApplications: member.customerApplications
        }));

        setMembersList(filteredMembersList);

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [context]);

  if (loading || !applicationsList.length || !membersList.length) {
    return null;
  }

  return (
    <ManageTeams {...({
      id: "ManageTeams",
      context,
      breaks,
      useLegacyEndpoints: false,
      hasSidebar: true,
      applicationsList,
      membersList
    } as ManageTeamsProps)}
      useEstimatedRowHeight />
  );
};

export default ManageTeamsTable;